<template>
  <div class="container-fluid">
    <div class="nd-form-standard">
      <div class="row-fluid">
        <select v-model="selectedSoftwareId" @change="handleSoftwareChange" class="span12">
          <option
            v-for="option in software"
            :key="option.id"
            :value="option.id"
          >
            {{ option.name }}
          </option>
        </select>
      </div>
      <div class="row-fluid">
        <SelectSearch id="software-group-select" v-if="isAutoType()"
          :defaultValue="defaultSelectedGroup" :options="filteredGroups"
          @search="handleSearchGroups" @select="handleSelectGroup" @resetOptions="handleResetFilteredGroups"
          placeholder="Select Group"
        />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, onBeforeMount, watch } from 'vue';
import { useRouter } from 'vue-router';
import ndGroupApiClient from '~/apiClients/NdGroupApiClient';
import SoftwareApiClient from '~/apiClients/SoftwareApiClient';
import { Software } from '~/models/Software';
import SelectSearch from '~/components/select/SelectSearch.vue';
import SelectedGroupHashChangeDispatcher from '~/windowEvents/SelectedGroupHashChangeDispatcher';
import SelectedSoftwareChangeDispatcher from '~/windowEvents/SelectedSoftwareChangeDispatcher';
import GroupHashHelper from '~/helpers/GroupHashHelper';
import PublishButtonStatusUpdate from "~/windowEvents/PublishButtonStatusUpdate";
import SoftwareHelper from '~/helpers/SoftwareHelper';

const software = ref<Software[]>([]);
const groups = ref<any[]>([]);
const filteredGroups = ref<any[]>([]);
const selectedSoftwareId = ref<number | null>(null);
const selectedSoftware = ref<any>({});
const selectedGroup = ref(GroupHashHelper.getGroupHash());
const defaultSelectedGroup = ref('');
const router = useRouter();

function handleSelectGroup(group: any) {
  selectedGroup.value = group.hash;
  GroupHashHelper.setSelectedGroupHash(selectedGroup.value);
  goToTrackerManagementPage();
  PublishButtonStatusUpdate.dispatchEvent();
}

function handleSearchGroups(query: string) {
  const lower = query.trim().toLowerCase();
  filteredGroups.value = groups.value.filter(group => {
    const name = group.name?.toLowerCase?.() || '';
    const hash = group.hash?.toLowerCase?.() || '';
    return name.includes(lower) || hash.includes(lower);
  });
}

function handleResetFilteredGroups() {
  filteredGroups.value = groups.value;
}

async function handleSoftwareChange() {
  selectedSoftware.value = software.value.find(s => s.id === selectedSoftwareId.value)?.safeAttributes;
  goToTrackerManagementPage();
}

function goToTrackerManagementPage() {
  router.replace({name: 'trackers'});
  SelectedGroupHashChangeDispatcher.dispatchEvent();
}

async function loadSoftwareList() {
  software.value = await (new SoftwareApiClient()).provideSoftware();
}

async function loadGroupList() {
  groups.value = await (new ndGroupApiClient()).provideGroups();
  filteredGroups.value = groups.value;
}

function isAutoType(): boolean {
  return SoftwareHelper.isAutoV10(selectedSoftware.value);
}

async function loadAndSetGroup() {
  await loadGroupList();
  GroupHashHelper.setSelectedGroupHash(selectedGroup.value);
  defaultSelectedGroup.value = groups.value.find(group => group.hash === selectedGroup.value)?.name ?? '';
}

async function init() {
  await loadSoftwareList();

  const stored = SoftwareHelper.getSelectedSoftwareObject();
  selectedSoftwareId.value = stored.id || software.value[0].id;
}

onBeforeMount(() => {
  window.addEventListener(SelectedSoftwareChangeDispatcher.EVENT_TYPE, async () => {
    await init();
  });
  init();
});

watch(selectedSoftwareId, async () => {
  selectedSoftware.value = software.value.find(s => s.id === selectedSoftwareId.value)?.safeAttributes;
  SoftwareHelper.setSelectedSoftware(selectedSoftware.value);
  isAutoType() ? await loadAndSetGroup() : GroupHashHelper.removeSelectedGroupHash();
});

</script>
